import React, { useRef, useMemo } from "react";
import { PolarArea } from "react-chartjs-2";
import { Chart, RadialLinearScale, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

Chart.register(RadialLinearScale, ArcElement, Tooltip, Legend, ChartDataLabels);

const fallbackColors = [
    "#aeea84", "#fcd39e", "#a8a1e9", "#ff6384", "#36a2eb", "#ffce56", "#b8e994",
    "#f6e58d", "#686de0", "#303952", "#f5cd79", "#574b90", "#f3a683", "#574b90"
];

// Utility to limit label length and add ellipsis
const truncateLabel = (str, maxLen = 32) => {
    if (typeof str !== "string") return "";
    return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
};

function downloadChartAsPng(chartRef, title = "chart") {
    const chartInstance = chartRef.current;
    if (!chartInstance) return;
    const canvas = chartInstance.canvas;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png", 1.0);
    link.download = `${title.replace(/\s+/g, "_")}.png`;
    link.click();
}

const whiteBackgroundPlugin = {
    id: 'custom_canvas_background_color',
    beforeDraw: (chart) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    }
}

export default function PolarAreaCenterLabelsChart({
    codes = [],
    files = [],
    mode = "codes-per-interview",
    title = ""
}) {
    const chartRef = useRef();

    const { labels, values, colors } = useMemo(() => {
        if (!Array.isArray(codes) || codes.length === 0) {
            return { labels: [], values: [], colors: [] };
        }
        
        try {
            const labels = codes.map(code => truncateLabel(code?.name || 'Unknown', 38));
            const colors = codes.map((code, idx) => code?.color || fallbackColors[idx % fallbackColors.length]);
            const values = codes.map(code => Array.isArray(code?.excerpts) ? code.excerpts.length : 0);
            return { labels, values, colors };
        } catch (error) {
            console.error('Error processing chart data:', error);
            return { labels: [], values: [], colors: [] };
        }
    }, [codes]);

    const data = {
        labels,
        datasets: [{
            label: "",
            data: values,
            backgroundColor: colors
        }]
    };

    const options = {
        responsive: false,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 50,
                bottom: 50
            }
        },
        scales: {
            r: {
                pointLabels: {
                    display: true,
                    centerPointLabels: true,
                    font: { size: 12, weight: "thin" },
                    padding: 18, // more padding for long labels
                },
                ticks: {
                    display: false // hides the circular ticks
                },
                beginAtZero: true
            }
        },
        plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: {
                display: true,
                color: "#000",
                font: { weight: "thin", size: 12 },
                formatter: (v) => (v > 0 ? v : ""),
                anchor: "middle",
                align: "middle",
                offset: 0,
                // Optional: adjust position dynamically if needed
            }
        }
    };

    // Don't render if there's no valid data
    if (!Array.isArray(codes) || codes.length === 0 || labels.length === 0) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <div className="border border-gray-300 shadow p-4 flex flex-col w-full justify-center items-center">
                    <div className="text-gray-500 italic text-center py-8">
                        No data available to display
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            <div>
                <div className="border border-gray-300 shadow p-4 flex flex-col w-full justify-center items-center">
                    <div
                        className="relative w-full flex justify-center"
                        style={{
                            maxWidth: "100%",
                        }}
                    >
                        <PolarArea
                            ref={chartRef}
                            data={data}
                            options={options}
                            height={600}
                            width={600}
                            plugins={[ChartDataLabels, whiteBackgroundPlugin]}
                            style={{ width: "600px", height: "600px" }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
